/**
 * The magazine spread — shared by the person gallery and the selection gallery.
 *
 * Both pages take a flat list of photos and lay it out as an editorial spread
 * rather than a grid. Extracted from PersonGallery unchanged so the two pages
 * cannot drift into two different rhythms.
 */

// Short editorial captions dropped between photo groups — deliberately generic
// (no gendered or event-specific language) so they read naturally under any
// cluster: a bride, a guest, a kid, a group of friends. Used only where the
// studio hasn't written its own caption for that section.
const CHERISH_WORDS = [
  "Some moments don't need explaining — you just had to be there.",
  "A single frame, holding a whole feeling.",
  "Joy has a way of finding the camera first.",
  "These are the seconds that outlast the day.",
  "Not posed. Not perfect. Just real.",
  "The kind of smile that only shows up once.",
  "Light finds its favorite people.",
  "Every photograph is a small forever.",
  "Some memories glow quietly, long after the noise fades.",
  "A story told frame by frame.",
  "This is what being present looks like.",
  "Caught mid-laugh, mid-love, mid-life.",
  "The best moments rarely wait to be arranged.",
  "Here, for one more look.",
  "Time moves. These stay.",
  "A little proof that it was beautiful.",
  "Some things are worth freezing in place.",
  "The quiet magic between the big moments.",
  "Nothing rehearsed. Everything felt.",
  "A page from a day that mattered.",
  "Held here, long after the moment passed.",
  "Small gestures, remembered forever.",
  "This is what belonging looks like.",
  "Framed not because it was perfect — because it was true.",
];

// Fisher–Yates shuffle, unbiased and side-effect free on the source array.
const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// Groups photos into an alternating rhythm of layouts (trio → duo → mosaic →
// feature → repeat) so the spread reads like a magazine, not a grid.
const LAYOUT_CYCLE = [
  { size: 3, layout: 'trio' },
  { size: 2, layout: 'duo' },
  { size: 4, layout: 'mosaic' },
  { size: 1, layout: 'feature' },
];

const buildChunks = (photos) => {
  const chunks = [];
  let i = 0, p = 0;
  while (i < photos.length) {
    const pattern = LAYOUT_CYCLE[p % LAYOUT_CYCLE.length];
    const slice = photos.slice(i, i + pattern.size);
    chunks.push({ layout: pattern.layout, photos: slice, startIndex: i });
    i += slice.length;
    p++;
  }
  return chunks;
};

/**
 * Orientation-aware spread.
 *
 * Every tile in a row shares the row's shape, so a row that mixes a portrait
 * with a landscape has to be wrong for one of them: fitted whole, the portrait
 * sat in a wide slot with large empty bands either side. Rows are therefore
 * built from runs of photos that already agree, and each run gets slots cut to
 * its own orientation.
 *
 * Photo order is preserved. Sorting portraits and landscapes into separate
 * passes would pack more tightly, but the selection gallery reads in upload
 * order — the day in sequence — and reordering it to suit the grid would put
 * the layout ahead of the story.
 */

// Near-squares go in landscape slots: a wide slot holds a square with modest
// bands, a tall one leaves them above and below where they read as a mistake.
const orientationOf = (ratio) => (ratio && ratio < 0.95 ? 'p' : 'l');

// Row sizes per orientation. Portraits are narrow, so more fit across; a
// landscape row of three would leave each photo small.
const CYCLE_BY_ORIENTATION = {
  p: [3, 2, 4],
  l: [2, 1, 2, 3],
};

const NAME_BY_SIZE = { 1: 'feature', 2: 'duo', 3: 'trio' };
const layoutName = (orientation, count) =>
  `${NAME_BY_SIZE[count] || 'mosaic'}-${orientation}`;

const buildChunksByOrientation = (photos, ratios) => {
  const chunks = [];
  const ratioOf = (photo) => (ratios && ratios.get ? ratios.get(photo.id) : null);
  let i = 0;
  let step = 0;

  while (i < photos.length) {
    const orientation = orientationOf(ratioOf(photos[i]));

    // How many photos from here already share this orientation.
    let run = 1;
    while (
      i + run < photos.length &&
      orientationOf(ratioOf(photos[i + run])) === orientation
    ) {
      run++;
    }

    const cycle = CYCLE_BY_ORIENTATION[orientation];
    let taken = 0;
    while (taken < run) {
      // Clamp to what is left of the run, so a row never borrows a photo of
      // the other orientation to fill itself out.
      const size = Math.min(cycle[step % cycle.length], run - taken);
      const slice = photos.slice(i + taken, i + taken + size);
      chunks.push({
        layout: layoutName(orientation, slice.length),
        photos: slice,
        startIndex: i + taken,
      });
      taken += size;
      step++;
    }
    i += run;
  }

  return chunks;
};

// One caption per chunk — a studio-written one where available, otherwise a
// random pick from CHERISH_WORDS (no repeats until the pool runs out).
const pickCaptions = (chunkCount, savedCaptions = []) => {
  let pool = shuffle(CHERISH_WORDS);
  const captions = [];
  for (let i = 0; i < chunkCount; i++) {
    if (savedCaptions[i] && savedCaptions[i].trim()) {
      captions.push(savedCaptions[i]);
      continue;
    }
    if (pool.length === 0) pool = shuffle(CHERISH_WORDS);
    captions.push(pool.pop());
  }
  return captions;
};

// How many photo groups render before the reader has scrolled. Each group is
// 1–4 photos plus its caption, so this is roughly a screen and a half.
const CHUNKS_PER_PAGE = 3;

// Reorders `photos` to match a saved id order; anything new (not yet in the
// saved order) is appended at the end in its original position, and stale
// ids that no longer exist (e.g. the photo was unselected by this viewer) are
// silently skipped.
const applyCustomOrder = (photos, order) => {
  if (!order || order.length === 0) return photos;
  const byId = new Map(photos.map((p) => [p.id, p]));
  const used = new Set();
  const ordered = [];
  for (const id of order) {
    if (byId.has(id) && !used.has(id)) {
      ordered.push(byId.get(id));
      used.add(id);
    }
  }
  for (const p of photos) {
    if (!used.has(p.id)) ordered.push(p);
  }
  return ordered;
};
export {
  CHERISH_WORDS,
  shuffle,
  LAYOUT_CYCLE,
  buildChunks,
  buildChunksByOrientation,
  orientationOf,
  pickCaptions,
  applyCustomOrder,
  CHUNKS_PER_PAGE,
};
